import { GeoMap } from '@delmonicos/shared-design-system-react/src/components/location/geo-map/geo-map.component';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { authenticate } from '@app/core/authentication/authentication.slice';
import { ConfigurationService } from '@app/core/configuration/configuration.service';
import { useAppDispatch } from '@app/core/hooks';
import { InAppStorage } from '@app/core/storage/in-app-storage';
import { LogoCPMS } from '@app/shared/logo-cpms/logo-cpms';

import * as css from './login.module.scss';
import { EmailPasswordStepController } from './step/email-password-step/email-password-step.controller';
import { OtpStepController } from './step/otp-step/otp-step.controller';

enum LoginStepEnum {
  PASSWORD_STEP,
  OTP_STEP,
}

interface LoginViewProps {
  handleForgottenPassword: () => void;
}

export const LoginView = ({
  handleForgottenPassword,
}: LoginViewProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [step, setStep] = useState<LoginStepEnum>(LoginStepEnum.PASSWORD_STEP);
  const [emailAddress, setEmailAddress] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [token, setToken] = useState<string>();
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const version = useMemo(() => ConfigurationService.getVersion(), []);
  const apiKey = ConfigurationService.getGoogleApiKey();

  function handleAuthenticate(jwt: string, refreshToken: string) {
    InAppStorage.setJwt(jwt, rememberMe);
    InAppStorage.setRefreshToken(refreshToken, rememberMe);
    dispatch(authenticate(jwt));
    // La redirection est gérée par LoginController via isAuth
  }

  function handleEmailPasswordStep(payload: {
    emailAddress: string;
    token: string;
    password: string;
  }) {
    setEmailAddress(payload.emailAddress);
    setToken(payload.token);
    setPassword(payload.password);
    setStep(LoginStepEnum.OTP_STEP);
  }

  function handleSendOtpError() {
    setStep(LoginStepEnum.PASSWORD_STEP);
    setEmailAddress(undefined);
    setPassword(undefined);
    setToken(undefined);
    toast.error(t('$.login.otp-step.error-send-otp'));
  }

  return (
    <div className={css.container}>
      <div className={css.content}>
        <LogoCPMS className={css.logo} />
        <div className={css.main}>
          {step === LoginStepEnum.PASSWORD_STEP ? (
            <EmailPasswordStepController
              onNext={handleEmailPasswordStep}
              onRememberMeChange={setRememberMe}
              onSendOtpError={handleSendOtpError}
              handleForgottenPassword={handleForgottenPassword}
            />
          ) : (
            <OtpStepController
              emailAddress={emailAddress}
              token={token}
              password={password}
              onNext={handleAuthenticate}
              onSendOtpError={handleSendOtpError}
            />
          )}
        </div>
        <Stack alignItems="flex-end">
          {version && (
            <Text fontSize={TextFontSize.Small}>
              {t('global.legal-text.version', { version })}
            </Text>
          )}
        </Stack>
      </div>
      {apiKey && (
        <GeoMap
          mapId="static-map-login"
          apiKey={apiKey}
          zoom={16}
          zoomable={false}
        />
      )}
    </div>
  );
};
